import {
    ACTION_TYPES_USER,
    type SessionState,
    type SessionData,
    type UserActionTypes
} from './user.types';

import { createContext, useEffect, useReducer } from 'react';
import {
    getActiveSessionIdentifier,
    setActiveSession
} from './setActiveSession';
import {
    getUserData,
    STORAGE_KEY_PREFIX,
    writeDataToStorage
} from './getUserData';

export const initialState: SessionState = {
    loading: false,
    loggedIn: false,
    loggedInStatus: 'initial',
    registered: false,
    senderPropertyStatus: null,
    creationDate: null,
    userId: null,
    userUuid: null,
    error: []
};

export const getUserAttempt = () => ({
    type: ACTION_TYPES_USER.GET_USER_ATTEMPT as const
});

export const getUserSuccess = (user?: SessionData) => ({
    type: ACTION_TYPES_USER.GET_USER_SUCCESS as const,
    user
});

export const getUserFailure = (error: string) => ({
    type: ACTION_TYPES_USER.GET_USER_FAILURE as const,
    error
});

export const setLoggedIn = (isLoggedIn: boolean) => ({
    type: ACTION_TYPES_USER.SET_LOGGED_IN as const,
    isLoggedIn
});

export const setUser = (user: SessionData) => ({
    type: ACTION_TYPES_USER.SET_USER as const,
    user
});

const userReducer = (
    state: SessionState = initialState,
    action: UserActionTypes
): SessionState => {
    switch (action.type) {
        case ACTION_TYPES_USER.GET_USER_ATTEMPT:
            return {
                ...state,
                loading: true,
                loggedInStatus: 'loading'
            };
        case ACTION_TYPES_USER.GET_USER_SUCCESS:
            return {
                ...state,
                loading: false,
                loggedIn: action.user?.loggedIn,
                loggedInStatus: action.user?.loggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn',
                senderPropertyStatus: action.user?.senderPropertyStatus,
                creationDate: action.user?.creationDate,
                userId: action.user?.userId,
                userUuid: action.user?.userUuid,
                registered: action.user?.registered,
                registrationSources: action.user?.registrationSources
            };
        case ACTION_TYPES_USER.GET_USER_FAILURE:
            return {
                ...state,
                loading: false,
                loggedInStatus: 'error',
                error: [action.error]
            };
        case ACTION_TYPES_USER.SET_LOGGED_IN:
            return {
                ...state,
                loggedIn: action.isLoggedIn,
                loggedInStatus: action.isLoggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn'
            };
        case ACTION_TYPES_USER.SET_USER:
            return {
                ...state,
                loggedIn: action.user?.loggedIn,
                loggedInStatus: action.user?.loggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn',
                senderPropertyStatus: action.user?.senderPropertyStatus,
                creationDate: action.user?.creationDate,
                userId: action.user?.userId,
                userUuid: action.user?.userUuid,
                registered: action.user?.registered,
                registrationSources: action.user?.registrationSources
            };
        default:
            return state;
    }
};

type Dispatch = (action: UserActionTypes) => void;

export const Context = createContext<{
    state: SessionState;
    dispatch: Dispatch;
}>({
    state: initialState,
    dispatch: () => void 0
});

type IdentifyTrait = Partial<{
    user_uuid: string | null;
    uuid: string | null;
    user_id: number | null;
    registered: boolean | null;
    signed_in_status: string;
    about_me_status: string | null;
    registration_platform: string | null;
    user_creation_date: string | null;
    consent_1: boolean;
    consent_2: boolean;
    consent_3: boolean;
    consent_4: boolean;
}>;

function identify(user: SessionData) {
    if (user.userUuid) {
        const sessionData = {
            user_uuid: user.userUuid,
            uuid: user.userUuid,
            user_id: user.userId,
            registered: user.registered,
            signed_in_status: user.loggedIn ? 'signed in' : 'signed out',
            about_me_status: user.senderPropertyStatus ?? '',
            registration_platform: user.registrationSources?.join(', '),
            user_creation_date: user.creationDate
        } as const;

        window.analytics?.ready(() => {
            const traits: IdentifyTrait =
                window.analytics?.user()?.traits() ?? {};
            const existingData: Record<string, unknown> = traits;
            const newData: Record<string, unknown> = {};

            for (const [key, value] of Object.entries(sessionData)) {
                if (value && value !== existingData[key]) {
                    newData[key] = value;
                }
            }

            if (sessionData.uuid) {
                window.analytics?.identify(sessionData.uuid, newData);
            }
        });
    }
}

export const Provider: React.FC<
    React.PropsWithChildren<{ state?: SessionState; forceRefresh?: boolean }>
> = ({ children, state: prefetchedState, forceRefresh }) => {
    const [state, dispatch] = useReducer(
        userReducer,
        prefetchedState ?? initialState
    );

    useEffect(() => {
        const controller = new AbortController();

        async function fetchUser(skipCache?: boolean) {
            dispatch(getUserAttempt());

            try {
                const { data, errors } = await getUserData({
                    abortSignal: controller.signal,
                    skipCache
                });

                if (errors) {
                    dispatch(getUserFailure(errors[0].message));
                    return;
                }

                if (data?.user && !errors) {
                    dispatch(getUserSuccess(data.user));
                    let sessionId = getActiveSessionIdentifier();

                    /* it is possible, via the intranet, to adopt a user session on the fly. So we can land in here (via skipCache)
                  and need to reconcile the active session */
                    if (data.user?.loggedIn && sessionId === 'anon') {
                        sessionId = setActiveSession();
                    }

                    /* fix old sessions */
                    if (!sessionId) {
                        if (data.user?.loggedIn) {
                            sessionId = setActiveSession();
                        } else {
                            sessionId = setActiveSession('anon');
                        }
                    }

                    if (sessionId !== 'anon') {
                        const cacheKey = STORAGE_KEY_PREFIX + sessionId;
                        writeDataToStorage(cacheKey, data?.user);
                    }

                    identify(data.user);
                }
            } catch (error) {
                /* don't attempt state change if we have unmounted */
                if (!controller.signal.aborted) {
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : 'Unknown error';
                    dispatch(getUserFailure(errorMessage));
                }
            }
        }
        if (forceRefresh || !prefetchedState) fetchUser(forceRefresh);

        if (!forceRefresh && prefetchedState) identify(prefetchedState);

        return () => {
            controller.abort();
        };
    }, [prefetchedState, forceRefresh, dispatch]);

    const value = { state, dispatch };
    return <Context.Provider value={value}>{children}</Context.Provider>;
};
